import { Polyline, CircleMarker, Tooltip } from 'react-leaflet';
import { useTheme } from '../../hooks/useTheme';
import { ShortestPath } from '../../types/homeBase';
import { formatDistance, formatDuration } from '../../utils/formatters';

interface ShortestPathLayerProps {
  shortestPath: ShortestPath;
}

/**
 * Map overlay for the drone's return path to home base.
 *
 * The path is routed around active exclusion geofences (no-fly zones), so it may
 * contain intermediate detour waypoints. Styling distinguishes it from traveled
 * routes (blue solid) and planned routes (green dashed):
 *   - Routed / direct: orange (#e65100 light / #ff8f00 dark), dot-dash pattern.
 *   - Fallback (routing could not avoid a zone): red, and flagged in the tooltip
 *     so the operator never mistakes a fail-safe straight line for a safe route.
 *
 * Waypoint circles mark the start (drone), any detour points, and the end (home).
 */
export const ShortestPathLayer: React.FC<ShortestPathLayerProps> = ({
  shortestPath,
}) => {
  const { isDarkMode } = useTheme();

  const routing = shortestPath.routing;
  const isFallback = routing?.fallback === true;

  const routedColor = isDarkMode ? '#ff8f00' : '#e65100';
  const fallbackColor = isDarkMode ? '#ef5350' : '#d32f2f';
  const pathColor = isFallback ? fallbackColor : routedColor;
  const homeColor = '#0066FF';

  const positions: [number, number][] = shortestPath.waypoints.map((wp) => [
    wp.latitude,
    wp.longitude,
  ]);

  if (positions.length < 2) {
    return null;
  }

  const dronePos = positions[0];
  const homePos = positions[positions.length - 1];
  // Intermediate detour points introduced to skirt exclusion zones.
  const detours = positions.slice(1, -1);

  const etaMinutes = shortestPath.estimated_flight_time_sec / 60;

  // Human-readable note describing how the path was produced.
  const routingNote = isFallback
    ? '⚠ Direct fallback — could not route around no-fly zones'
    : routing && routing.avoided_zones > 0
      ? `Routed around ${routing.avoided_zones} no-fly zone${
          routing.avoided_zones === 1 ? '' : 's'
        }`
      : null;

  return (
    <>
      {/* Return path line */}
      <Polyline
        positions={positions}
        color={pathColor}
        weight={3}
        opacity={0.85}
        dashArray={isFallback ? '4 6' : '6 4 2 4'}
      />

      {/* Drone position waypoint */}
      <CircleMarker
        center={dronePos}
        radius={7}
        fillColor={pathColor}
        fillOpacity={0.9}
        color="#ffffff"
        weight={2}
      >
        <Tooltip direction="top" offset={[0, -8]}>
          <div>
            <strong>Drone position</strong>
            <br />
            Path start
          </div>
        </Tooltip>
      </CircleMarker>

      {/* Detour waypoints (routed around exclusion zones) */}
      {detours.map((pos, i) => (
        <CircleMarker
          key={`detour-${i}`}
          center={pos}
          radius={4}
          fillColor={pathColor}
          fillOpacity={0.9}
          color="#ffffff"
          weight={1.5}
        >
          <Tooltip direction="top" offset={[0, -6]}>
            <div>
              <strong>Detour waypoint {i + 1}</strong>
              <br />
              Avoiding a no-fly zone
            </div>
          </Tooltip>
        </CircleMarker>
      ))}

      {/* Home base endpoint */}
      <CircleMarker
        center={homePos}
        radius={7}
        fillColor={homeColor}
        fillOpacity={0.9}
        color="#ffffff"
        weight={2}
      >
        <Tooltip direction="top" offset={[0, -8]}>
          <div>
            <strong>Home base</strong>
            <br />
            {formatDistance(shortestPath.distance_km)} &bull;{' '}
            {formatDuration(etaMinutes)}
            {routingNote && (
              <>
                <br />
                {routingNote}
              </>
            )}
          </div>
        </Tooltip>
      </CircleMarker>
    </>
  );
};
